import React, { useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import Swal from "sweetalert2";
import { Bold, Eye, Italic, Pencil, Trash2, Underline, X } from "lucide-react";
import "./GestionEmails.css";

import {
    getAllEmailTemplatesRequest,
    createEmailTemplateRequest,
    updateEmailTemplateRequest,
    deleteEmailTemplateRequest,
    getEmailPlaceholdersRequest,
    clearEmailMessage,
    clearEmailError,
} from "../../../redux/actions/emailActions";

const CONTEXT_LABELS = {
    GENERIC: "Générique",
    USER: "Utilisateur",
    FORMATION: "Formation",
    CANDIDATURE: "Candidature",
    DEMANDE_RECRUTEMENT: "Demande recrutement",
    CAMPAGNE: "Campagne",
};

const CONTEXT_TYPES = Object.keys(CONTEXT_LABELS);

const CONTEXT_CONFIG = {
    GENERIC: { color: "#6b7280", bg: "#f3f4f6" },
    USER: { color: "#2a5ca9", bg: "#e8edf7" },
    FORMATION: { color: "#059669", bg: "#d1fae5" },
    CANDIDATURE: { color: "#d97706", bg: "#fef3c7" },
    DEMANDE_RECRUTEMENT: { color: "#d97706", bg: "#fef3c7" },
    CAMPAGNE: { color: "#b91c1c", bg: "#fee2e2" },
};

const emptyForm = {
    code: "",
    title: "",
    description: "",
    subject: "",
    body: "",
    contextType: "USER",
    isActive: true,
};

const normalizeTemplateForForm = (template) => ({
    code: template?.code || "",
    title: template?.title || "",
    description: template?.description || "",
    subject: template?.subject || "",
    body: template?.body || "",
    contextType: template?.contextType || "USER",
    isActive: template?.isActive ?? true,
});

const getPlainEmailBody = (body = "") => {
    if (!body) return "";
    const htmlWithBreaks = String(body).replace(/<(br\s*\/?>|\/p|\/div|\/li|\/h[1-6])>/gi, "\n");
    const container = document.createElement("div");
    container.innerHTML = htmlWithBreaks;
    return (container.textContent || container.innerText || "").replace(/\n\s*\n\s*\n+/g, "\n\n").trim();
};

const GestionEmails = ({
                           templates,
                           placeholders,
                           loading,
                           message,
                           error,
                           getAllEmailTemplates,
                           createEmailTemplate,
                           updateEmailTemplate,
                           deleteEmailTemplate,
                           getEmailPlaceholders,
                           clearMessage,
                           clearError,
                       }) => {
    const [q, setQ] = useState("");
    const [filterContext, setFilterContext] = useState("Tous");
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [previewing, setPreviewing] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [activeTab, setActiveTab] = useState("infos");

    useEffect(() => {
        getAllEmailTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!isModalOpen) return;
        if (form.contextType) {
            getEmailPlaceholders(form.contextType);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isModalOpen, form.contextType]);

    useEffect(() => {
        if (!message) return;

        if (
            message.toLowerCase().includes("créé") ||
            message.toLowerCase().includes("mis à jour")
        ) {
            closeModal();
        }

        const timer = setTimeout(() => {
            clearMessage();
        }, 2500);

        return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [message]);

    useEffect(() => {
        if (!error) return;

        const timer = setTimeout(() => {
            clearError();
        }, 3500);

        return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [error]);

    const filtered = useMemo(() => {
        const term = q.trim().toLowerCase();

        return (templates || []).filter((tpl) => {
            const matchQ =
                !term ||
                (tpl.code || "").toLowerCase().includes(term) ||
                (tpl.title || "").toLowerCase().includes(term) ||
                (tpl.description || "").toLowerCase().includes(term) ||
                (tpl.subject || "").toLowerCase().includes(term);

            const matchContext =
                filterContext === "Tous" || tpl.contextType === filterContext;

            return matchQ && matchContext;
        });
    }, [templates, q, filterContext]);

    const openCreate = () => {
        setEditing(null);
        setForm(emptyForm);
        setActiveTab("infos");
        setIsModalOpen(true);
    };

    const openEdit = (template) => {
        setEditing(template);
        setForm(normalizeTemplateForForm(template));
        setActiveTab("infos");
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setEditing(null);
        setForm(emptyForm);
        setActiveTab("infos");
    };

    const openPreview = (template) => setPreviewing(template);
    const closePreview = () => setPreviewing(null);

    const onChange = (e) => {
        const { name, value, type, checked } = e.target;
        const nextValue = type === "checkbox" ? checked : value;

        setForm((prev) => ({
            ...prev,
            [name]: nextValue,
        }));
    };

    const textareaRef = useRef(null);

    const applyFormat = (tag) => {
        const el = textareaRef.current;
        if (!el) return;
        const start = el.selectionStart;
        const end = el.selectionEnd;
        const text = form.body || "";
        const selected = text.substring(start, end);
        const formatted = `<${tag}>${selected || "texte"}</${tag}>`;
        const newText = text.substring(0, start) + formatted + text.substring(end);
        setForm((prev) => ({ ...prev, body: newText }));
        setTimeout(() => {
            el.focus();
            const newCursorPos = start + tag.length + 2 + (selected || "texte").length;
            el.setSelectionRange(newCursorPos, newCursorPos);
        }, 0);
    };

    const insertVar = (value) => {
        const el = textareaRef.current;
        if (!el) {
            setForm((prev) => ({ ...prev, body: `${prev.body || ""}${value}` }));
            return;
        }
        const start = el.selectionStart;
        const end = el.selectionEnd;
        const text = form.body || "";
        const newText = text.substring(0, start) + value + text.substring(end);
        setForm((prev) => ({ ...prev, body: newText }));
        setTimeout(() => {
            el.focus();
            el.setSelectionRange(start + value.length, start + value.length);
        }, 0);
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!form.code.trim()) {
            Swal.fire({ icon: "warning", text: "Le code est obligatoire." });
            return;
        }

        if (!form.title.trim()) {
            Swal.fire({ icon: "warning", text: "Le titre est obligatoire." });
            return;
        }

        if (!form.subject.trim()) {
            Swal.fire({ icon: "warning", text: "Le sujet est obligatoire." });
            return;
        }

        if (!form.body.trim()) {
            Swal.fire({ icon: "warning", text: "Le corps de l'email est obligatoire." });
            return;
        }

        const payload = {
            code: form.code.trim(),
            title: form.title.trim(),
            description: form.description?.trim() || "",
            subject: form.subject,
            body: form.body,
            contextType: form.contextType,
            isActive: form.isActive,
        };

        if (editing?.id) {
            updateEmailTemplate(editing.id, payload);
        } else {
            createEmailTemplate(payload);
        }
    };

    const handleDelete = (template) => {
        Swal.fire({
            title: "Supprimer",
            text: `Supprimer le modèle "${template.title}" ?`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Oui, supprimer",
            cancelButtonText: "Annuler",
            confirmButtonColor: "#b91c1c",
        }).then((res) => {
            if (res.isConfirmed) {
                deleteEmailTemplate(template.id);
            }
        });
    };

    const handleToggleActive = (template) => {
        updateEmailTemplate(template.id, {
            code: template.code,
            title: template.title,
            description: template.description || "",
            subject: template.subject,
            body: template.body,
            contextType: template.contextType,
            isActive: !template.isActive,
        });
    };

    return (
        <div className="ge-page">
            <div className="ge-header">
                <div>
                    <h2>Gestion des emails</h2>
                    <p className="ge-subtitle">
                        Personnalisez vos modèles d'emails de communication
                    </p>
                </div>

                <button className="ge-btn ge-btn-primary" onClick={openCreate}>
                    + Ajouter un email
                </button>
            </div>

            <div className="ge-toolbar">
                <input
                    className="ge-search"
                    placeholder="Rechercher par code, titre, sujet..."
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                />

                <div className="ge-filter-tabs">
                    {["Tous", ...CONTEXT_TYPES].map((ctx) => (
                        <button
                            key={ctx}
                            className={`ge-filter-tab ${filterContext === ctx ? "active" : ""}`}
                            onClick={() => setFilterContext(ctx)}
                        >
                            {ctx === "Tous" ? "Tous" : CONTEXT_LABELS[ctx]}
                        </button>
                    ))}
                </div>

                <div className="ge-meta">
                    {filtered.length} modèle{filtered.length !== 1 ? "s" : ""}
                </div>
            </div>

            {message && <div className="ge-alert ge-alert-success">{message}</div>}
            {error && <div className="ge-alert ge-alert-danger">{error}</div>}

            <div className="ge-table-wrapper">
                {loading ? (
                    <div className="ge-empty">Chargement...</div>
                ) : (
                    <table className="ge-table">
                        <thead>
                        <tr>
                            <th>Code</th>
                            <th>Titre</th>
                            <th>Sujet</th>
                            <th>Contexte</th>
                            <th>Description</th>
                            <th>Statut</th>
                            <th style={{ width: 160, textAlign: "right" }}>Actions</th>
                        </tr>
                        </thead>

                        <tbody>
                        {filtered.map((template) => {
                            const cfg = CONTEXT_CONFIG[template.contextType] || {};

                            return (
                                <tr key={template.id}>
                                    <td>
                                        <strong>{template.code}</strong>
                                    </td>

                                    <td>
                                        <div className="ge-titre-cell">
                                            <div className="ge-email-dot" />
                                            <span className="ge-titre">{template.title}</span>
                                        </div>
                                    </td>

                                    <td className="ge-sujet">{template.subject}</td>

                                    <td>
                                            <span
                                                className="ge-badge"
                                                style={{ color: cfg.color, background: cfg.bg }}
                                            >
                                                {CONTEXT_LABELS[template.contextType] || template.contextType}
                                            </span>
                                    </td>

                                    <td className="ge-desc">{template.description || "—"}</td>

                                    <td>
                                        <button
                                            className={`ge-toggle ${template.isActive ? "on" : "off"}`}
                                            onClick={() => handleToggleActive(template)}
                                            title={template.isActive ? "Désactiver" : "Activer"}
                                        >
                                            <span className="ge-toggle-knob" />
                                            <span className="ge-toggle-label">
                                                    {template.isActive ? "Actif" : "Inactif"}
                                                </span>
                                        </button>
                                    </td>

                                    <td className="ge-row-actions">
                                        <button
                                            type="button"
                                            className="ge-icon-btn"
                                            onClick={() => openPreview(template)}
                                            title="Voir le détail de l'email"
                                            aria-label="Voir le détail de l'email"
                                        >
                                            <Eye size={15} />
                                        </button>

                                        <button
                                            type="button"
                                            className="ge-icon-btn ge-icon-btn--edit"
                                            onClick={() => openEdit(template)}
                                            title="Modifier"
                                            aria-label="Modifier"
                                        >
                                            <Pencil size={15} />
                                        </button>

                                        <button
                                            type="button"
                                            className="ge-icon-btn ge-icon-btn--danger"
                                            onClick={() => handleDelete(template)}
                                            title="Supprimer"
                                            aria-label="Supprimer"
                                        >
                                            <Trash2 size={15} />
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}

                        {filtered.length === 0 && (
                            <tr>
                                <td colSpan={7} className="ge-empty">
                                    Aucun modèle d'email trouvé
                                </td>
                            </tr>
                        )}
                        </tbody>
                    </table>
                )}
            </div>

            {isModalOpen && (
                <div className="ge-overlay" onClick={closeModal}>
                    <div className="ge-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="ge-modal-header">
                            <h3>
                                {editing
                                    ? "Modifier le modèle d'email"
                                    : "Ajouter un modèle d'email"}
                            </h3>
                            <button className="ge-modal-close" onClick={closeModal}>
                                ✕
                            </button>
                        </div>

                        <div className="ge-tabs">
                            <button
                                type="button"
                                className={`ge-tab ${activeTab === "infos" ? "active" : ""}`}
                                onClick={() => setActiveTab("infos")}
                            >
                                Informations générales
                            </button>
                            <button
                                type="button"
                                className={`ge-tab ${activeTab === "corps" ? "active" : ""}`}
                                onClick={() => setActiveTab("corps")}
                            >
                                Corps de l'email
                            </button>
                        </div>

                        <form className="ge-form" onSubmit={handleSubmit}>
                            {activeTab === "infos" && (
                                <div className="ge-grid">
                                    <div className="ge-field">
                                        <label>Code *</label>
                                        <input
                                            name="code"
                                            value={form.code}
                                            onChange={onChange}
                                            placeholder="Ex : ACCOUNT_ACTIVATION"
                                            disabled={!!editing}
                                        />
                                        <span className="ge-hint">
                                            Code unique du template
                                        </span>
                                    </div>

                                    <div className="ge-field">
                                        <label>Contexte *</label>
                                        <select
                                            name="contextType"
                                            value={form.contextType}
                                            onChange={onChange}
                                        >
                                            {CONTEXT_TYPES.map((ctx) => (
                                                <option key={ctx} value={ctx}>
                                                    {CONTEXT_LABELS[ctx]}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="ge-field ge-full">
                                        <label>Titre *</label>
                                        <input
                                            name="title"
                                            value={form.title}
                                            onChange={onChange}
                                            placeholder="Ex : Activation de compte"
                                        />
                                    </div>

                                    <div className="ge-field ge-full">
                                        <label>Sujet *</label>
                                        <input
                                            name="subject"
                                            value={form.subject}
                                            onChange={onChange}
                                            placeholder="Ex : Activation de votre compte {{societe}}"
                                        />
                                        <span className="ge-hint">
                                            Utilisez {"{{variable}}"} pour les champs dynamiques
                                        </span>
                                    </div>

                                    <div className="ge-field ge-full">
                                        <label>Description</label>
                                        <textarea
                                            name="description"
                                            value={form.description}
                                            onChange={onChange}
                                            rows={3}
                                            placeholder="Décrivez l'usage de ce modèle..."
                                        />
                                    </div>

                                    <div className="ge-field ge-full">
                                        <label className="ge-check-label">
                                            <input
                                                type="checkbox"
                                                name="isActive"
                                                checked={form.isActive}
                                                onChange={onChange}
                                            />
                                            Email actif (envoi activé)
                                        </label>
                                    </div>
                                </div>
                            )}

                            {activeTab === "corps" && (
                                <div className="ge-grid">
                                    <div className="ge-field ge-full">
                                        <div className="ge-corps-label-row">
                                            <label>Corps de l'email *</label>

                                            <div className="ge-editor-tools">
                                                <div className="ge-format-btns">
                                                    <button
                                                        type="button"
                                                        className="ge-format-btn"
                                                        onClick={() => applyFormat("b")}
                                                        title="Gras (<b>)"
                                                    >
                                                        <Bold size={14} />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="ge-format-btn"
                                                        onClick={() => applyFormat("i")}
                                                        title="Italique (<i>)"
                                                    >
                                                        <Italic size={14} />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="ge-format-btn"
                                                        onClick={() => applyFormat("u")}
                                                        title="Souligné (<u>)"
                                                    >
                                                        <Underline size={14} />
                                                    </button>
                                                </div>

                                                <div className="ge-vars">
                                                    <span className="ge-vars-label">Insérer :</span>

                                                    {(placeholders || []).length > 0 ? (
                                                        placeholders.map((p) => (
                                                            <button
                                                                key={p.key}
                                                                type="button"
                                                                className="ge-var-chip"
                                                                title={p.label || p.key}
                                                                onClick={() => insertVar(p.key)}
                                                            >
                                                                {p.key}
                                                            </button>
                                                        ))
                                                    ) : (
                                                        <span className="ge-hint">
                                                            Aucune variable disponible pour ce contexte
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        <textarea
                                            ref={textareaRef}
                                            name="body"
                                            value={form.body}
                                            onChange={onChange}
                                            rows={12}
                                            placeholder={
                                                "Rédigez le corps de l'email...\n\nUtilisez les boutons B / I / U pour le formatage ou insérez des variables dynamiques."
                                            }
                                            className="ge-textarea-mono"
                                        />
                                    </div>

                                    {form.body && (
                                        <div className="ge-field ge-full">
                                            <label>Aperçu</label>
                                            <div className="ge-preview">
                                                <div className="ge-preview-bar">
                                                    <span className="ge-dot red" />
                                                    <span className="ge-dot yellow" />
                                                    <span className="ge-dot green" />
                                                    <span className="ge-preview-label">
                                                        Aperçu du message
                                                    </span>
                                                </div>

                                                <div className="ge-preview-content">
                                                    <p className="ge-preview-sujet">
                                                        <b>Objet :</b> {form.subject || "—"}
                                                    </p>
                                                    <div 
                                                        className="ge-preview-rendered"
                                                        dangerouslySetInnerHTML={{
                                                            __html: (form.body || "").replace(/\n/g, "<br/>")
                                                        }} 
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            <div className="ge-modal-actions">
                                <button
                                    type="button"
                                    className="ge-btn ge-btn-outline"
                                    onClick={closeModal}
                                    disabled={loading}
                                >
                                    Annuler
                                </button>

                                <button
                                    type="submit"
                                    className="ge-btn ge-btn-primary"
                                    disabled={loading}
                                >
                                    {loading
                                        ? "Chargement..."
                                        : editing
                                            ? "Enregistrer"
                                            : "Créer l'email"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {previewing && (
                <div className="ge-overlay" onClick={closePreview}>
                    <div className="ge-modal ge-modal--preview" onClick={(e) => e.stopPropagation()}>
                        <div className="ge-modal-header">
                            <div>
                                <h3>Détail de l'email</h3>
                                <p className="ge-modal-subtitle">{previewing.title || "Modèle d'email"}</p>
                            </div>
                            <button type="button" className="ge-modal-close" onClick={closePreview} aria-label="Fermer">
                                <X size={19} />
                            </button>
                        </div>

                        <div className="ge-email-detail">
                            <div className="ge-email-detail-row"><span>Code</span><b>{previewing.code || "—"}</b></div>
                            <div className="ge-email-detail-row"><span>Sujet</span><b>{previewing.subject || "—"}</b></div>
                            {previewing.description && <div className="ge-email-detail-row"><span>Description</span><b>{previewing.description}</b></div>}
                            <div className="ge-email-detail-body">
                                <span>Aperçu du message</span>
                                <div 
                                    className="ge-preview-rendered"
                                    dangerouslySetInnerHTML={{
                                        __html: (previewing.body || "").replace(/\n/g, "<br/>")
                                    }}
                                />
                            </div>
                        </div>

                        <div className="ge-modal-actions">
                            <button type="button" className="ge-btn ge-btn-outline" onClick={closePreview}>Fermer</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const mapStateToProps = (state) => ({
    templates: state.emails?.templates || [],
    placeholders: state.emails?.placeholders || [],
    loading: state.emails?.loading || false,
    message: state.emails?.message || "",
    error: state.emails?.error || null,
});

const mapDispatchToProps = (dispatch) => ({
    getAllEmailTemplates: () => dispatch(getAllEmailTemplatesRequest()),
    createEmailTemplate: (data) => dispatch(createEmailTemplateRequest(data)),
    updateEmailTemplate: (id, data) => dispatch(updateEmailTemplateRequest(id, data)),
    deleteEmailTemplate: (id) => dispatch(deleteEmailTemplateRequest(id)),
    getEmailPlaceholders: (contextType) =>
        dispatch(getEmailPlaceholdersRequest(contextType)),
    clearMessage: () => dispatch(clearEmailMessage()),
    clearError: () => dispatch(clearEmailError()),
});

export default connect(mapStateToProps, mapDispatchToProps)(GestionEmails);
